import axios from 'axios';
import defaultLogo from '../assets/Empower-logo.png';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { environment } from '../env/environment';
import { validatePassword, passwordRequirements } from '../utils/validation';
import { escapeHtml } from '../utils/sanitize';
import { useTenant } from '../context/TenantContext';

axios.defaults.withCredentials = true;

const SetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const resetToken = location.state?.resetToken;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Get the logo to display
  const rawLogo = tenant?.signinLogoUrl || tenant?.logoUrl || defaultLogo;
  const isDefaultLogo = !tenant?.signinLogoUrl && !tenant?.logoUrl;
  const logoToDisplay = rawLogo && !rawLogo.startsWith('data:') ? rawLogo + (rawLogo.includes('?') ? '&' : '?') + '_v=' + Date.now() : rawLogo;

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    const validation = validatePassword(pwd);
    setPasswordErrors(validation.errors);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setMessage('');
      setLoading(false);
      return;
    }

    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError('Password does not meet requirements: ' + validation.errors.join(', '));
      setMessage('');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${environment.baseUrl}/api/auth/reset-password`, {
        resetToken,
        newPassword
      });
      
      setMessage(response.data.message);
      setError('');

      // ✅ Redirect to login page after successful password reset
      setTimeout(() => {
        navigate('/', { state: { message: 'Password reset successfully! Please login with your new password.' } });
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  // ✅ If no resetToken, redirect back to forgot password
  if (!resetToken) {
    navigate('/forgot-password');
    return null;
  }

  return (
    <div className="signin-container">
      <div className='bg-image-container'>
        <img
          src={logoToDisplay}
          alt={tenant?.brandName || 'Logo'}
          className={isDefaultLogo ? 'default-logo' : ''}
        />
      </div>
      <div className='form-container'>
        <form className="form" onSubmit={handleResetPassword}>
          <button
            type="button"
            className="back-btn forgot-btn"
            onClick={() => navigate('/')}
          >
            <ArrowBackIosNewIcon className='arrow-icon' /> Back to Login
          </button>

          <div className='signin-heading'>
            <h1>Reset Password</h1>
            <p>Enter your new password below</p>
          </div>

          <div>
            <div className="flex-column">
              <label>New Password <span style={{color:'red'}}>*</span></label>
            </div>
            <div className="inputForm">
              <input
                type="password"
                value={newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password"
                className="input"
                required
                minLength="8"
              />
            </div>
            {newPassword && passwordErrors.length > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                <div style={{ marginBottom: '3px' }}>Password requirements:</div>
                {passwordRequirements.map((req, idx) => (
                  <div key={idx} style={{ color: req.test(newPassword) ? 'green' : 'red' }}>
                    {req.test(newPassword) ? '✓' : '✗'} {req.message}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex-column">
              <label>Confirm Password <span style={{color:'red'}}>*</span></label>
            </div>
            <div className="inputForm">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="input"
                required
                minLength="8"
              />
            </div>
          </div>

          {message && <p className="success-message">{message}</p>}
          {error && <p className="error-message">{error}</p>}

          <button 
            type="submit" 
            className="button-submit"
            disabled={loading || !newPassword.trim() || !confirmPassword.trim()}
            style={{
              opacity: loading || !newPassword.trim() || !confirmPassword.trim() ? 0.6 : 1,
              cursor: loading || !newPassword.trim() || !confirmPassword.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;